import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import '../styles/styles.css';

const Nutrition = () => {
    const [dailyLog, setDailyLog] = useState([]);
    const [nutritionGoals, setNutritionGoals] = useState({
        calories: 2000,
        protein: 150,
        carbs: 200,
        fat: 65,
        fiber: 25
    });
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [showAddMeal, setShowAddMeal] = useState(false);
    const { user } = useAuth();

    // Placeholder daily log data
    const placeholderLog = [
        {
            id: 1,
            recipe: "Overnight Oats with Berries",
            servingSize: 1,
            calories: 320,
            protein: 12,
            carbs: 45,
            fat: 8,
            fiber: 6,
            mealType: "breakfast",
            time: "08:30"
        },
        {
            id: 2,
            recipe: "Grilled Chicken Salad",
            servingSize: 1.5,
            calories: 450,
            protein: 35,
            carbs: 15,
            fat: 25,
            fiber: 8,
            mealType: "lunch",
            time: "12:45"
        }
    ];

    useEffect(() => {
        // TODO: Fetch daily log from API based on selectedDate and user
        setDailyLog(placeholderLog);
    }, [selectedDate, user]);

    const calculateTotals = () => {
        return dailyLog.reduce((totals, entry) => ({
            calories: totals.calories + (entry.calories * entry.servingSize),
            protein: totals.protein + (entry.protein * entry.servingSize),
            carbs: totals.carbs + (entry.carbs * entry.servingSize),
            fat: totals.fat + (entry.fat * entry.servingSize),
            fiber: totals.fiber + (entry.fiber * entry.servingSize)
        }), { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
    };

    const totals = calculateTotals();

    const getNutritionPercentage = (current, goal) => {
        return Math.min((current / goal) * 100, 100);
    };

    const addMealFromRecipe = (recipeId, servingSize, mealType) => {
        // TODO: Implement adding meal from recipe
        // This would fetch nutrition data from recipe and add to daily log
        console.log('Adding meal:', { recipeId, servingSize, mealType });
        setShowAddMeal(false);
    };

    if (!user) {
        return (
            <div className="nutrition-container">
                <div className="auth-prompt">
                    <h2>Login Required</h2>
                    <p>Please log in to track your nutrition and access personalized macro goals.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="nutrition-container">
            <div className="nutrition-header">
                <h1>Nutrition Tracking</h1>
                <div className="date-selector">
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="date-input"
                    />
                </div>
            </div>

            {/* Daily Overview */}
            <div className="nutrition-overview">
                <h2>Daily Overview</h2>
                <div className="macro-grid">
                    <div className="macro-card">
                        <h3>Calories</h3>
                        <div className="macro-progress">
                            <div className="macro-bar">
                                <div 
                                    className="macro-fill calories" 
                                    style={{ width: `${getNutritionPercentage(totals.calories, nutritionGoals.calories)}%` }}
                                ></div>
                            </div>
                            <span className="macro-text">
                                {Math.round(totals.calories)} / {nutritionGoals.calories}
                            </span>
                        </div>
                    </div>

                    <div className="macro-card">
                        <h3>Protein</h3>
                        <div className="macro-progress">
                            <div className="macro-bar">
                                <div 
                                    className="macro-fill protein" 
                                    style={{ width: `${getNutritionPercentage(totals.protein, nutritionGoals.protein)}%` }}
                                ></div>
                            </div>
                            <span className="macro-text">
                                {Math.round(totals.protein)}g / {nutritionGoals.protein}g
                            </span>
                        </div>
                    </div>

                    <div className="macro-card">
                        <h3>Carbs</h3>
                        <div className="macro-progress">
                            <div className="macro-bar">
                                <div 
                                    className="macro-fill carbs" 
                                    style={{ width: `${getNutritionPercentage(totals.carbs, nutritionGoals.carbs)}%` }}
                                ></div>
                            </div>
                            <span className="macro-text">
                                {Math.round(totals.carbs)}g / {nutritionGoals.carbs}g
                            </span>
                        </div>
                    </div>

                    <div className="macro-card">
                        <h3>Fat</h3>
                        <div className="macro-progress">
                            <div className="macro-bar">
                                <div 
                                    className="macro-fill fat" 
                                    style={{ width: `${getNutritionPercentage(totals.fat, nutritionGoals.fat)}%` }}
                                ></div>
                            </div>
                            <span className="macro-text">
                                {Math.round(totals.fat)}g / {nutritionGoals.fat}g
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Daily Log */}
            <div className="daily-log">
                <div className="log-header">
                    <h2>Today's Meals</h2>
                    <button 
                        onClick={() => setShowAddMeal(true)}
                        className="add-meal-btn"
                    >
                        + Add Meal
                    </button>
                </div>

                <div className="meals-list">
                    {dailyLog.length === 0 ? (
                        <div className="no-meals">
                            <p>No meals logged for this day yet.</p>
                            <button 
                                onClick={() => setShowAddMeal(true)}
                                className="btn-large"
                            >
                                Log Your First Meal
                            </button>
                        </div>
                    ) : (
                        dailyLog.map((entry) => (
                            <div key={entry.id} className="meal-entry">
                                <div className="meal-info">
                                    <h4>{entry.recipe}</h4>
                                    <span className="meal-type">{entry.mealType}</span>
                                    <span className="meal-time">{entry.time}</span>
                                </div>
                                <div className="meal-nutrition">
                                    <span>Serving: {entry.servingSize}x</span>
                                    <span>{Math.round(entry.calories * entry.servingSize)} cal</span>
                                    <span>P: {Math.round(entry.protein * entry.servingSize)}g</span>
                                    <span>C: {Math.round(entry.carbs * entry.servingSize)}g</span>
                                    <span>F: {Math.round(entry.fat * entry.servingSize)}g</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Add Meal Modal Placeholder */}
            {showAddMeal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Add Meal</h3>
                        <p>This feature will allow you to:</p>
                        <ul>
                            <li>Search your recipes and add them as meals</li>
                            <li>Specify serving sizes</li>
                            <li>Choose meal type (breakfast, lunch, dinner, snack)</li>
                            <li>Automatically calculate nutrition from recipe data</li>
                        </ul>
                        <div className="modal-actions">
                            <button onClick={() => setShowAddMeal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Nutrition; 